import React from "react";

interface OptionSelectorProps {
  options: string[];
  value: string;
  onSelect: (selectedOption: string | undefined) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "success" | "warning" | "error";
  disabled?: boolean;
}

const OptionSelector: React.FC<OptionSelectorProps> = ({
  options,
  onSelect,
  value,
  className = "",
  size = "md",
  variant = "default",
  disabled = false
}) => {
  const handleOptionClick = (option: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    if (value !== option) {
      onSelect(option);
    }
  };

  const sizeClasses = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  const variantClasses = {
    default: {
      base: "text-secondary-700 bg-white border-secondary-300 hover:bg-secondary-50 hover:border-secondary-400",
      selected: "text-primary-700 bg-primary-50 border-primary-500 ring-2 ring-primary-200"
    },
    primary: {
      base: "text-primary-700 bg-primary-50 border-primary-300 hover:bg-primary-100 hover:border-primary-400",
      selected: "text-white bg-primary-600 border-primary-600 ring-2 ring-primary-300"
    },
    success: {
      base: "text-success-700 bg-success-50 border-success-300 hover:bg-success-100 hover:border-success-400",
      selected: "text-white bg-success-600 border-success-600 ring-2 ring-success-300"
    },
    warning: {
      base: "text-warning-700 bg-warning-50 border-warning-300 hover:bg-warning-100 hover:border-warning-400",
      selected: "text-white bg-warning-600 border-warning-600 ring-2 ring-warning-300"
    },
    error: {
      base: "text-error-700 bg-error-50 border-error-300 hover:bg-error-100 hover:border-error-400",
      selected: "text-white bg-error-600 border-error-600 ring-2 ring-error-300"
    }
  };

  const baseClasses = `
    font-medium border rounded-lg transition-all duration-200 
    focus:outline-none focus:ring-0
    disabled:opacity-50 disabled:cursor-not-allowed
    transform hover:scale-105 active:scale-95
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {options.map((option) => {
        const isSelected = value === option;
        const variantClass = isSelected ? variantClasses[variant].selected : variantClasses[variant].base;

        return (
          <button
            key={option}
            onClick={(event) => handleOptionClick(option, event)}
            className={`${baseClasses} ${sizeClasses[size]} ${variantClass}`}
            type="button"
            disabled={disabled}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

export { OptionSelector };
